class CollectionFiltersGrid extends HTMLElement {
  connectedCallback () {
    this.container = this
    this.wrapper = this.querySelector('[data-grid-wrapper]')
    this.grid = this.querySelector('[data-grid]')

    // Get sticky behavior setting
    this.stickyBehavior = this.getAttribute('data-sticky-behavior') || 'sticky'

    // Track initialization phase to prevent unwanted scrolling
    this.isInitializing = true

    this.addEventListener('click', (e) => this.onContainerClick(e))
    this.boundOnDocumentClick = this.onDocumentClick.bind(this)
    this._documentClickBound = false
    this.setupDropdowns()
    this.setupPriceFilterRow()
    this.setupSortDropdown()

    // Check for load_previous parameter and restore filters if needed
    const shouldRestoreFilters = this.checkForLoadPrevious()

    // Only sync from URL if we didn't restore filters
    if (!shouldRestoreFilters) {
      this.syncFromUrl()
    }

    // SEO elements
    const h1El = this.querySelector('[data-dynamic-h1]')
    this.initialH1 = h1El?.getAttribute('data-base-h1') || h1El?.textContent?.trim()
    this.defaultTitle = this.getAttribute('data-title-default') || document.title
    const seoTitlesRaw = this.getAttribute('data-seo-category-titles')
    this.seoCategoryTitles = {}
    if (seoTitlesRaw) {
      try {
        this.seoCategoryTitles = JSON.parse(seoTitlesRaw)
      } catch (e) {
        this.seoCategoryTitles = {}
      }
    }
    this.updateSeoFromSelection()

    // Initialize reset button visibility
    this.updateResetButtonVisibility()

    // Initialize clear filter options visibility
    this.updateClearFilterVisibility()

    // Initial counts should already be correct from server-side rendering

    // Listen for our own loading events to manage grid busy state
    window.addEventListener('filters:loading', () => this.setBusy(true))
    window.addEventListener('filters:loaded', () => this.setBusy(false))

    // Handle sticky behavior based on setting
    this.handleStickyBehavior()

    // Mark initialization as complete
    this.isInitializing = false

    // Set up product card click handlers for localStorage
    this.setupProductCardHandlers()

    // Initialize price bracket counts cache
    this.priceFilterEnabled = this.getAttribute('data-price-filter-enabled') === 'true'
    this.priceBracketCountsCache = new Map()
    this.priceBracketCountsFetching = false
  }

  buildCleanUrl (pathname, params) {
    const paramString = params.toString()
    return paramString ? `${pathname}?${paramString}` : pathname
  }

  setCanonicalTag (href) {
    try {
      let canonicalUrl
      if (href) {
        // If href is a relative URL (starts with /), make it absolute
        if (href.startsWith('/')) {
          canonicalUrl = `${window.location.origin}${href}`
        } else {
          // If it's already absolute, use as-is
          canonicalUrl = href
        }
      } else {
        // Fallback to current location
        canonicalUrl = window.location.href
      }

      let link = document.head.querySelector('link[rel="canonical"]')
      const isServerSideCanonical = link?.getAttribute('data-source') === 'server-side'

      if (!link) {
        link = document.createElement('link')
        link.setAttribute('rel', 'canonical')
        document.head.appendChild(link)
      }

      // Only update if it's not a server-side canonical or if we're explicitly setting a new URL
      if (!isServerSideCanonical || href) {
        link.setAttribute('href', canonicalUrl)
        link.setAttribute('data-source', 'client-side')
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[CollectionFiltersGrid] setCanonicalTag error', e)
    }
  }

  setupDropdowns () {
    // Get overlay element
    this.overlay = this.querySelector('[data-dropdown-overlay]')

    this.querySelectorAll('.collection-header__filters-item').forEach((item) => {
      const button = item.querySelector('.collection-header__filter')
      const dropdown = item.querySelector('.collection-header__filter-dropdown')
      if (!button || !dropdown) return

      button.addEventListener('click', (e) => {
        e.preventDefault()
        const isOpen = !dropdown.classList.contains('hidden')
        this.closeAll()
        if (!isOpen) {
          dropdown.classList.remove('hidden')
          button.setAttribute('aria-expanded', 'true')
          this.showOverlay()
          this.bindDocumentOutsideClick()
          if (window.innerWidth < 1280) {
            this.scrollToTop()
          }
          const filterGroup = button.dataset.filterGroup
          if (filterGroup === 'price') {
            this.fetchPriceBracketCounts()
          }
        }
      })

      dropdown.addEventListener('click', (e) => {
        const optionBtn = e.target.closest('.collection-header__filter-option-btn')
        if (optionBtn) {
          this.onOptionSelect(item, optionBtn)
        }
      })
    })

    const resetBtns = this.querySelectorAll('[data-action="reset-filters"]')
    resetBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.resetFilters())
    })

    const clearBtns = this.querySelectorAll('[data-action="clear-filter"]')
    clearBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        this.onClearFilter(btn)
      })
    })

    // Add overlay click handler
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.closeAll()
      })
    }
  }

  setupPriceFilterRow () {
    this.priceFilterRow = this.querySelector('[data-price-filter-row]')
    if (!this.priceFilterRow) return

    this.priceScrollContainer = this.priceFilterRow.querySelector('[data-price-scroll-container]')
    this.priceFaderLeft = this.priceFilterRow.querySelector('[data-price-fader-left]')
    this.priceFaderRight = this.priceFilterRow.querySelector('[data-price-fader-right]')

    if (this.priceScrollContainer) {
      this.priceScrollContainer.addEventListener('scroll', () => this.updatePriceFaders(), { passive: true })
      window.addEventListener('resize', () => this.updatePriceFaders())
      this.updatePriceFaders()
    }

    const priceBtns = this.priceFilterRow.querySelectorAll('.collection-header__price-btn')
    priceBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const clickedClear = e.target.closest('[data-price-btn-clear]')
        if (clickedClear && btn.getAttribute('aria-pressed') === 'true') {
          this.clearPriceFilter()
        } else {
          this.onPriceButtonClick(btn)
        }
      })
    })
  }

  updatePriceFaders () {
    if (!this.priceScrollContainer || !this.priceFaderLeft || !this.priceFaderRight) return

    const container = this.priceScrollContainer
    const isScrollable = container.scrollWidth > container.clientWidth

    if (!isScrollable) {
      this.priceFaderLeft.classList.add('opacity-0')
      this.priceFaderLeft.classList.remove('opacity-100')
      this.priceFaderRight.classList.add('opacity-0')
      this.priceFaderRight.classList.remove('opacity-100')
      return
    }

    const scrollLeft = container.scrollLeft
    const clientWidth = container.clientWidth
    const scrollWidth = container.scrollWidth

    const atStart = scrollLeft <= 0
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1

    this.priceFaderLeft.classList.toggle('opacity-0', atStart)
    this.priceFaderLeft.classList.toggle('opacity-100', !atStart)

    this.priceFaderRight.classList.toggle('opacity-0', atEnd)
    this.priceFaderRight.classList.toggle('opacity-100', !atEnd)
  }

  onPriceButtonClick (btn) {
    const wasActive = btn.getAttribute('aria-pressed') === 'true'
    const gteValue = btn.dataset.gte || ''
    const lteValue = btn.dataset.lte || ''

    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)

    params.delete('filter.v.price.gte')
    params.delete('filter.v.price.lte')

    if (!wasActive) {
      if (gteValue) params.append('filter.v.price.gte', gteValue)
      if (lteValue) params.append('filter.v.price.lte', lteValue)
    }
    params.delete('page')

    const nextUrl = this.buildCleanUrl(url.pathname, params)
    window.history.replaceState({}, '', nextUrl)

    this.updatePriceButtonStates(!wasActive ? btn : null)

    // Scroll the clicked button to center
    if (!wasActive) {
      this.scrollPriceButtonToCenter(btn)
    }

    this.fetchAndSwap(nextUrl)
    this.scrollToTop()
    this.updateSeoFromSelection()
  }

  scrollPriceButtonToCenter (btn) {
    if (!this.priceScrollContainer) return

    const container = this.priceScrollContainer
    const btnLeft = btn.offsetLeft
    const btnWidth = btn.offsetWidth
    const containerWidth = container.clientWidth

    let newScrollLeft = btnLeft - containerWidth / 2 + btnWidth / 2

    newScrollLeft = Math.max(0, Math.min(newScrollLeft, container.scrollWidth - containerWidth))

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    })
  }

  updatePriceButtonStates (activeBtn) {
    if (!this.priceFilterRow) return

    const priceStyle = this.priceFilterRow.dataset.priceStyle || 'pill'
    const hasActiveFilter = activeBtn !== null

    this.priceFilterRow.querySelectorAll('.collection-header__price-btn').forEach(btn => {
      const isActive = btn === activeBtn
      btn.setAttribute('aria-pressed', String(isActive))

      // Show/hide inline clear icon
      const clearIcon = btn.querySelector('[data-price-btn-clear]')
      if (clearIcon) {
        clearIcon.classList.toggle('block', isActive)
        clearIcon.classList.toggle('hidden', !isActive)
      }

      if (priceStyle === 'pill') {
        btn.classList.toggle('bg-black', isActive)
        btn.classList.toggle('text-white', isActive)
        btn.classList.toggle('border-black', isActive)
        btn.classList.toggle('bg-white', !isActive)
        btn.classList.toggle('text-black', !isActive)
        btn.classList.toggle('border-clay', !isActive)
        btn.classList.remove('opacity-70')
        if (hasActiveFilter && !isActive) {
          btn.classList.add('opacity-70')
        }
      } else {
        btn.classList.toggle('underline', isActive)
        btn.classList.toggle('underline-offset-4', isActive)
        btn.classList.remove('opacity-70')
        if (hasActiveFilter && !isActive) {
          btn.classList.add('opacity-70')
        }
      }
    })

    this.updateResetButtonVisibility()
  }

  clearPriceFilter () {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)

    params.delete('filter.v.price.gte')
    params.delete('filter.v.price.lte')
    params.delete('page')

    const nextUrl = this.buildCleanUrl(url.pathname, params)
    window.history.replaceState({}, '', nextUrl)

    this.updatePriceButtonStates(null)

    this.fetchAndSwap(nextUrl)
    this.scrollToTop()
    this.updateSeoFromSelection()
  }

  setupSortDropdown () {
    this.sortWrapper = this.querySelector('[data-sort-wrapper]')
    if (!this.sortWrapper) return

    this.sortTrigger = this.sortWrapper.querySelector('[data-sort-trigger]')
    this.sortDropdown = this.sortWrapper.querySelector('[data-sort-dropdown]')
    this.sortCurrentLabel = this.sortWrapper.querySelector('[data-sort-current]')

    if (this.sortTrigger && this.sortDropdown) {
      this.sortTrigger.addEventListener('click', (e) => {
        e.preventDefault()
        const isOpen = !this.sortDropdown.classList.contains('hidden')
        this.closeSortDropdown()
        if (!isOpen) {
          this.sortDropdown.classList.remove('hidden')
          this.sortTrigger.setAttribute('aria-expanded', 'true')
          this.bindDocumentOutsideClick()
        }
      })

      this.sortDropdown.querySelectorAll('.product-grid__sort-option-btn').forEach(btn => {
        btn.addEventListener('click', () => this.onSortOptionSelect(btn))
      })
    }
  }

  closeSortDropdown () {
    if (this.sortDropdown) {
      this.sortDropdown.classList.add('hidden')
    }
    if (this.sortTrigger) {
      this.sortTrigger.setAttribute('aria-expanded', 'false')
    }
  }

  onSortOptionSelect (btn) {
    const sortValue = btn.dataset.sortValue
    if (!sortValue) return

    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)

    params.set('sort_by', sortValue)
    params.delete('page')

    const nextUrl = this.buildCleanUrl(url.pathname, params)
    window.history.replaceState({}, '', nextUrl)

    this.sortDropdown.querySelectorAll('.product-grid__sort-option-btn').forEach(optBtn => {
      const isActive = optBtn === btn
      optBtn.setAttribute('aria-selected', String(isActive))
      optBtn.classList.toggle('bg-oat', isActive)
      optBtn.classList.toggle('font-medium', isActive)
    })

    if (this.sortCurrentLabel) {
      this.sortCurrentLabel.textContent = btn.textContent.trim()
    }

    this.closeSortDropdown()
    this.fetchAndSwap(nextUrl, false)
  }

  onContainerClick (e) {}

  bindDocumentOutsideClick () {
    if (this._documentClickBound) return
    document.addEventListener('click', this.boundOnDocumentClick)
    this._documentClickBound = true
  }

  unbindDocumentOutsideClick () {
    if (!this._documentClickBound) return
    document.removeEventListener('click', this.boundOnDocumentClick)
    this._documentClickBound = false
  }

  syncDocumentOutsideClick () {
    const sortOpen = this.sortDropdown && !this.sortDropdown.classList.contains('hidden')
    if (this.hasOpenDropdown() || sortOpen) {
      this.bindDocumentOutsideClick()
    } else {
      this.unbindDocumentOutsideClick()
    }
  }

  onDocumentClick (e) {
    const items = this.querySelectorAll('.collection-header__filters-item')
    let anyDropdownOpen = false

    items.forEach((item) => {
      const button = item.querySelector('.collection-header__filter')
      const dropdown = item.querySelector('.collection-header__filter-dropdown')
      if (!dropdown || dropdown.classList.contains('hidden')) return

      anyDropdownOpen = true
      const clickedInsideDropdown = dropdown.contains(e.target)
      const clickedButton = button && button.contains(e.target)

      if (!clickedInsideDropdown && !clickedButton) {
        dropdown.classList.add('hidden')
        button?.setAttribute('aria-expanded', 'false')
      }
    })

    // Hide overlay if no dropdowns are open after processing clicks
    if (!anyDropdownOpen || !this.hasOpenDropdown()) {
      this.hideOverlay()
    }

    // Close sort dropdown if clicking outside
    if (this.sortWrapper && this.sortDropdown && !this.sortDropdown.classList.contains('hidden')) {
      const clickedInsideSort = this.sortWrapper.contains(e.target)
      if (!clickedInsideSort) {
        this.closeSortDropdown()
      }
    }

    this.syncDocumentOutsideClick()
  }

  closeAll () {
    this.querySelectorAll('.collection-header__filters-item').forEach((item) => {
      const button = item.querySelector('.collection-header__filter')
      const dropdown = item.querySelector('.collection-header__filter-dropdown')
      if (dropdown) dropdown.classList.add('hidden')
      if (button) button.setAttribute('aria-expanded', 'false')
    })
    this.hideOverlay()
    this.unbindDocumentOutsideClick()
  }

  onOptionSelect (item, optionBtn) {
    const group = item.querySelector('.collection-header__filter')?.dataset.filterGroup
    const param = item.querySelector('.collection-header__filter')?.dataset.paramName
    if (!group) return

    const wasActive = optionBtn.getAttribute('aria-pressed') === 'true'
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)

    if (group === 'price') {
      const gteValue = optionBtn.dataset.gte
      const lteValue = optionBtn.dataset.lte

      params.delete('filter.v.price.gte')
      params.delete('filter.v.price.lte')

      if (!wasActive && (gteValue || lteValue)) {
        if (gteValue) params.append('filter.v.price.gte', gteValue)
        if (lteValue) params.append('filter.v.price.lte', lteValue)
      }
    } else {
      if (!param) return
      const selectedValue = optionBtn.dataset.value

      Array.from(params.keys()).forEach((key) => {
        if (key === param) params.delete(key)
      })
      if (!wasActive && selectedValue) {
        params.append(param, selectedValue)
      }
    }
    params.delete('page')

    const nextUrl = this.buildCleanUrl(url.pathname, params)
    window.history.replaceState({}, '', nextUrl)

    item.querySelectorAll('.collection-header__filter-option-btn').forEach((btn) => {
      const isActive = !wasActive && btn === optionBtn
      btn.setAttribute('aria-pressed', String(isActive))
      btn.classList.toggle('bg-oat', isActive)
      const tick = btn.querySelector('.collection-header__filter-tick')
      if (tick) tick.classList.toggle('invisible', !isActive)
    })

    if (group === 'price') {
      const gteValue = optionBtn.dataset.gte
      const lteValue = optionBtn.dataset.lte
      this.updateButtonLabel(item, group, wasActive ? [] : [{ gte: gteValue, lte: lteValue }])
    } else {
      const selectedValue = optionBtn.dataset.value
      this.updateButtonLabel(item, group, wasActive ? [] : [selectedValue])
    }

    const dropdown = item.querySelector('.collection-header__filter-dropdown')
    const trigger = item.querySelector('.collection-header__filter')
    if (dropdown && trigger) {
      dropdown.classList.add('hidden')
      trigger.setAttribute('aria-expanded', 'false')
    }

    this.fetchAndSwap(nextUrl)
    this.scrollToTop()
    this.updateSeoFromSelection()
  }

  onClearFilter (clearBtn) {
    const item = clearBtn.closest('.collection-header__filters-item')
    if (!item) return

    const group = item.querySelector('.collection-header__filter')?.dataset.filterGroup
    const param = item.querySelector('.collection-header__filter')?.dataset.paramName

    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)

    if (group === 'price') {
      params.delete('filter.v.price.gte')
      params.delete('filter.v.price.lte')
    } else {
      if (!param) return
      Array.from(params.keys()).forEach((key) => {
        if (key === param) params.delete(key)
      })
    }
    params.delete('page')

    const nextUrl = this.buildCleanUrl(url.pathname, params)
    window.history.replaceState({}, '', nextUrl)

    item.querySelectorAll('.collection-header__filter-option-btn').forEach((btn) => {
      btn.setAttribute('aria-pressed', 'false')
      btn.classList.remove('bg-oat')
      const tick = btn.querySelector('.collection-header__filter-tick')
      if (tick) tick.classList.add('invisible')
    })

    this.updateButtonLabel(item, group, [])

    // Close dropdown if the clear button was clicked from inside a dropdown
    const dropdown = item.querySelector('.collection-header__filter-dropdown')
    const trigger = item.querySelector('.collection-header__filter')
    if (dropdown && trigger) {
      dropdown.classList.add('hidden')
      trigger.setAttribute('aria-expanded', 'false')
      // Hide overlay when dropdown is closed
      this.hideOverlay()
    }

    this.fetchAndSwap(nextUrl)
    this.scrollToTop()
    this.updateSeoFromSelection()
  }

  resetFilters () {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)
    // Remove only native filter params
    Array.from(params.keys()).forEach((key) => {
      if (key.startsWith('filter.')) params.delete(key)
    })
    params.delete('page')
    const nextUrl = this.buildCleanUrl(url.pathname, params)
    window.history.replaceState({}, '', nextUrl)
    this.clearSelections()
    // Also clear price filter row
    if (this.priceFilterRow) {
      this.updatePriceButtonStates(null)
    }
    this.fetchAndSwap(nextUrl, false)
    this.updateSeoFromSelection(true)
  }

  clearSelections () {
    this.querySelectorAll('.collection-header__filters-item').forEach((item) => {
      item.querySelectorAll('.collection-header__filter-option-btn').forEach((btn) => {
        btn.setAttribute('aria-pressed', 'false')
        btn.classList.remove('bg-oat')
        const tick = btn.querySelector('.collection-header__filter-tick')
        if (tick) tick.classList.add('invisible')
      })
      const group = item.querySelector('.collection-header__filter')?.dataset.filterGroup
      this.updateButtonLabel(item, group, [])
    })
    this.updateResetButtonVisibility()
    this.updateClearFilterVisibility()
  }

  syncFromUrl () {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)
    const priceGte = params.get('filter.v.price.gte') || ''
    const priceLte = params.get('filter.v.price.lte') || ''

    this.querySelectorAll('.collection-header__filters-item').forEach((item) => {
      const group = item.querySelector('.collection-header__filter')?.dataset.filterGroup
      const param = item.querySelector('.collection-header__filter')?.dataset.paramName

      if (group === 'price') {
        item.querySelectorAll('.collection-header__filter-option-btn').forEach((btn) => {
          if (btn.dataset.action === 'clear-filter') return
          const btnGte = btn.dataset.gte || ''
          const btnLte = btn.dataset.lte || ''
          const isActive = priceGte === btnGte && priceLte === btnLte
          btn.setAttribute('aria-pressed', String(isActive))
          btn.classList.toggle('bg-oat', isActive)
        })
        const hasPriceFilter = priceGte || priceLte
        this.updateButtonLabel(item, group, hasPriceFilter ? [{ gte: priceGte, lte: priceLte }] : [])
      } else {
        if (!param) return
        const selected = params.get(param) ? [params.get(param)] : []
        item.querySelectorAll('.collection-header__filter-option-btn').forEach((btn) => {
          const isActive = selected.includes(btn.dataset.value)
          btn.setAttribute('aria-pressed', String(isActive))
          btn.classList.toggle('bg-oat', isActive)
          const tick = btn.querySelector('.collection-header__filter-tick')
          if (tick) tick.classList.toggle('invisible', !isActive)
        })
        this.updateButtonLabel(item, group, selected)
      }
    })

    // Sync price filter row buttons
    if (this.priceFilterRow) {
      let activeBtn = null
      this.priceFilterRow.querySelectorAll('.collection-header__price-btn').forEach(btn => {
        const btnGte = btn.dataset.gte || ''
        const btnLte = btn.dataset.lte || ''
        if (priceGte === btnGte && priceLte === btnLte && (priceGte || priceLte)) {
          activeBtn = btn
        }
      })
      this.updatePriceButtonStates(activeBtn)
    }

    this.updateSeoFromSelection()
    this.updateResetButtonVisibility()
    this.updateClearFilterVisibility()
  }

  updateButtonLabel (item, group, values) {
    const button = item.querySelector('.collection-header__filter')
    const labelEl = button?.querySelector('.collection-header__filter-label')
    const countEl = button?.querySelector('.collection-header__filter-count-number')
    const chevronEl = button?.querySelector('.collection-header__chevron')
    const clearBtn = item.querySelector('.collection-header__filter-clear')

    if (!button || !labelEl) return
    const base = labelEl.dataset.baseText || labelEl.textContent.trim()
    labelEl.dataset.baseText = base
    const count = Array.isArray(values) ? values.length : 0
    const hasSelection = count > 0

    let text = base
    if (count === 1) {
      const selectedValue = values[0]
      let matchBtn = null
      let optionLabel = null

      if (group === 'price' && typeof selectedValue === 'object') {
        const gteVal = selectedValue.gte || ''
        const lteVal = selectedValue.lte || ''
        matchBtn = item.querySelector(`.collection-header__filter-option-btn[data-gte="${CSS.escape(gteVal)}"][data-lte="${CSS.escape(lteVal)}"]`)
        optionLabel = matchBtn?.querySelector('.collection-header__filter-option-label')?.textContent?.trim()
      } else {
        matchBtn = item.querySelector(`.collection-header__filter-option-btn[data-value="${CSS.escape(selectedValue)}"]`)
        optionLabel = matchBtn?.querySelector('.collection-header__filter-option-label')?.textContent?.trim()
      }

      if (optionLabel) text = optionLabel

      if (countEl) {
        const optionCount = matchBtn?.querySelector('.collection-header__filter-option-count-number')?.textContent?.trim()
        if (optionCount) {
          countEl.textContent = optionCount
          countEl.classList.remove('hidden')
        }
      }
    } else {
      text = base
      if (countEl) {
        countEl.classList.add('hidden')
      }
    }

    labelEl.textContent = text
    if (count === 1) {
      labelEl.classList.add('font-medium')
    } else {
      labelEl.classList.remove('font-medium')
    }

    // Show/hide chevron and clear button based on selection
    if (chevronEl && clearBtn) {
      if (hasSelection) {
        chevronEl.classList.add('hidden')
        clearBtn.classList.remove('hidden')
        clearBtn.classList.add('flex')
      } else {
        chevronEl.classList.remove('hidden')
        clearBtn.classList.add('hidden')
        clearBtn.classList.remove('flex')
      }
    }

    button.toggleAttribute('data-has-selection', count > 0)

    // Update reset button visibility based on any active filters
    this.updateResetButtonVisibility()

    // Update clear filter options visibility
    this.updateClearFilterVisibility()
  }

  updateResetButtonVisibility () {
    // Check if any filters are active (including price filter row)
    let hasActiveFilters = this.querySelectorAll('.collection-header__filter[data-has-selection]').length > 0

    // Also check price filter row
    if (this.priceFilterRow) {
      const activePriceBtn = this.priceFilterRow.querySelector('.collection-header__price-btn[aria-pressed="true"]')
      if (activePriceBtn) {
        hasActiveFilters = true
      }
    }

    // Get reset button wrappers
    const mobileResetWrapper = this.querySelector('.collection-header__reset-wrapper--mobile')
    const desktopResetWrapper = this.querySelector('.collection-header__reset-wrapper--desktop')

    if (mobileResetWrapper) {
      if (hasActiveFilters) {
        mobileResetWrapper.classList.remove('hidden')
        mobileResetWrapper.classList.add('flex')
      } else {
        mobileResetWrapper.classList.add('hidden')
        mobileResetWrapper.classList.remove('flex')
      }
    }

    if (desktopResetWrapper) {
      if (hasActiveFilters) {
        // Show on desktop: remove xl:hidden and add xl:flex
        desktopResetWrapper.classList.remove('xl:hidden')
        desktopResetWrapper.classList.add('xl:flex')
      } else {
        // Hide on desktop: add xl:hidden and remove xl:flex
        desktopResetWrapper.classList.add('xl:hidden')
        desktopResetWrapper.classList.remove('xl:flex')
      }
    }
  }

  updateClearFilterVisibility () {
    // Update clear filter options visibility for both model and category filters
    this.querySelectorAll('.collection-header__filters-item').forEach((item) => {
      const filterGroup = item.querySelector('.collection-header__filter')?.dataset.filterGroup
      if (!filterGroup) return

      // Find the clear filter button in this dropdown
      const clearFilterBtn = item.querySelector('.collection-header__filter-option-btn[data-action="clear-filter"]')
      const clearFilterLi = clearFilterBtn?.closest('li')
      const dividerLi = clearFilterLi?.nextElementSibling

      if (!clearFilterBtn || !clearFilterLi) return

      // Check if any option in this filter group is active
      const hasActiveOption = item.querySelector('.collection-header__filter-option-btn[aria-pressed="true"]:not([data-action="clear-filter"])')

      if (hasActiveOption) {
        // Show clear filter option and its divider
        clearFilterLi.style.display = ''
        if (dividerLi && dividerLi.classList.contains('border-t')) {
          dividerLi.style.display = ''
        }
      } else {
        // Hide clear filter option and its divider
        clearFilterLi.style.display = 'none'
        if (dividerLi && dividerLi.classList.contains('border-t')) {
          dividerLi.style.display = 'none'
        }
      }
    })
  }

  setFiltersDisabled (isDisabled) {
    const method = isDisabled ? 'add' : 'remove'
    this.querySelectorAll('.collection-header__filter, [data-action="reset-filters"], [data-action="clear-filter"]').forEach((el) => {
      if (isDisabled) {
        el.setAttribute('disabled', 'true')
      } else {
        el.removeAttribute('disabled')
      }
      el.classList[method]('pointer-events-none')
    })

    // Toggle count spinners on disable/enable - mobile filter buttons
    this.querySelectorAll('.collection-header__filter-count').forEach((wrap) => {
      const num = wrap.querySelector('.collection-header__filter-count-number')
      const spin = wrap.querySelector('.collection-header__filter-count-spinner')
      if (!num || !spin) return
      if (isDisabled) {
        num.classList.add('hidden')
        spin.classList.remove('hidden')
      } else {
        spin.classList.add('hidden')
        const filterButton = wrap.closest('.collection-header__filter')
        const hasSelection = filterButton?.hasAttribute('data-has-selection')
        num.classList.toggle('hidden', !hasSelection)
      }
    })

    // Toggle count spinners on disable/enable - mobile dropdown options
    this.querySelectorAll('.collection-header__filter-option-count').forEach((wrap) => {
      const num = wrap.querySelector('.collection-header__filter-option-count-number')
      const spin = wrap.querySelector('.collection-header__filter-option-count-spinner')
      if (!num || !spin) return
      if (isDisabled) {
        num.classList.add('hidden')
        spin.classList.remove('hidden')
      } else {
        num.classList.remove('hidden')
        spin.classList.add('hidden')
      }
    })
  }

  setBusy (isBusy) {
    if (!this.wrapper) return
    this.wrapper.setAttribute('aria-busy', String(isBusy))
  }

  showOverlay () {
    if (this.overlay) {
      this.overlay.classList.remove('hidden')
    }
  }

  hideOverlay () {
    if (this.overlay) {
      this.overlay.classList.add('hidden')
    }
  }

  hasOpenDropdown () {
    const dropdowns = this.querySelectorAll('.collection-header__filter-dropdown')
    return Array.from(dropdowns).some(dropdown => !dropdown.classList.contains('hidden'))
  }

  scrollToTop () {
    // Don't scroll during initialization (page load with existing filters)
    if (this.isInitializing) return

    try {
      const filtersEl = this.querySelector('.collection-header__filters-sticky')

      if (!filtersEl) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        return
      }

      // Get the sticky top value (dynamically calculated)
      let stickyTopValue = 60 // Default from CSS
      if (filtersEl.hasAttribute('data-adjusted-for-promo')) {
        // Parse the actual computed top value when adjusted for promo bar
        const computedStyle = getComputedStyle(filtersEl)
        const topValue = computedStyle.top
        if (topValue && topValue !== 'auto') {
          stickyTopValue = parseInt(topValue, 10) || 60
        }
      }

      // Find the original position where the sticky element becomes sticky
      // Use the parent container or header to calculate this since the sticky element
      // might already be "stuck" and we can't rely on its current position
      const headerInner = this.querySelector('.collection-header__inner')
      let originalStickyPosition = 0

      if (headerInner) {
        // Calculate based on header height + any spacing
        const headerRect = headerInner.getBoundingClientRect()
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop
        originalStickyPosition = currentScrollTop + headerRect.bottom
      } else {
        // Fallback: temporarily disable sticky to get original position
        const originalPosition = filtersEl.style.position
        const originalTop = filtersEl.style.top

        filtersEl.style.position = 'static'
        filtersEl.style.top = 'auto'

        const rect = filtersEl.getBoundingClientRect()
        originalStickyPosition = (window.pageYOffset || document.documentElement.scrollTop) + rect.top

        // Restore original sticky properties
        filtersEl.style.position = originalPosition
        filtersEl.style.top = originalTop
      }

      // Calculate the target scroll position where filters become sticky
      const targetScrollPosition = originalStickyPosition - stickyTopValue

      // Always scroll to this position, whether coming from above or below
      window.scrollTo({ top: Math.max(0, targetScrollPosition), left: 0, behavior: 'smooth' })
    } catch (error) {
      // Fallback for older browsers or errors
      const filtersEl = this.querySelector('.collection-header__filters-sticky')

      if (filtersEl) {
        // Use a simple approach: find the header inner and calculate from there
        const headerInner = this.querySelector('.collection-header__inner')
        if (headerInner) {
          const rect = headerInner.getBoundingClientRect()
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop
          const headerBottom = scrollTop + rect.bottom
          const fallbackStickyTop = filtersEl.hasAttribute('data-adjusted-for-promo') ? 100 : 60
          const targetPosition = headerBottom - fallbackStickyTop
          window.scrollTo(0, Math.max(0, targetPosition))
        } else {
          window.scrollTo(0, 0)
        }
      } else {
        window.scrollTo(0, 0)
      }
    }
  }

  async fetchAndSwap (url, shouldScroll = true) {
    const sectionDomId = this.closest('[id^="shopify-section-"]')?.id
    const sectionKey = sectionDomId ? sectionDomId.replace(/^shopify-section-/, '') : null

    if (!sectionKey) return

    const requestedKeys = [sectionKey]

    const endLoading = () => {
      this.setFiltersDisabled(false)
      window.filterBus?.emitChange({ url })
      window.filterBus?.emitLoaded({ url })
      window.dispatchEvent(new CustomEvent('filters:loaded', { detail: { url, requestedKeys } }))
      this.updateSeoFromSelection()
      this.setCanonicalTag(this.buildCanonicalUrl(url))
      this.invalidatePriceBracketCountsCache()
    }

    // Always enter busy state; exit only after counts are fully applied
    try {
      window.filterBus?.emitLoading({ url, requestedKeys })
      window.dispatchEvent(new CustomEvent('filters:loading', { detail: { url, requestedKeys } }))
      this.setFiltersDisabled(true)

      const minDelay = new Promise((resolve) => setTimeout(resolve, 100))

      // Check if we need to include universal products
      const needsUniversalProducts = this.shouldIncludeUniversalProducts(url)

      const fetchPromise = window.filterFetch?.fetchSections(requestedKeys, url)
      let universalPromise = null

      if (needsUniversalProducts.needed) {
        universalPromise = window.filterFetch?.fetchSections(requestedKeys, needsUniversalProducts.universalUrl)
      }

      const results = await Promise.all([fetchPromise, universalPromise, minDelay].filter(Boolean))
      const data = results[0]
      const universalData = results[1] || null

      if (!data) {
        this.scrollToTop()
        endLoading()
        return
      }

      const sectionHtml = data[sectionKey]
      if (sectionHtml) {
        let finalSectionHtml = sectionHtml

        if (universalData && universalData[sectionKey]) {
          const universalSectionHtml = universalData[sectionKey]
          if (universalSectionHtml) {
            finalSectionHtml = this.mergeGridResults(sectionHtml, universalSectionHtml)
          }
        }

        const temp = document.createElement('div')
        temp.innerHTML = finalSectionHtml

        // Update product count (now in grid toolbar)
        const newCountEl = temp.querySelector('[data-product-count]')
        const curCountEl = this.querySelector('[data-product-count]')
        if (newCountEl && curCountEl) {
          curCountEl.textContent = newCountEl.textContent
        }

        // Update filter counts from the new HTML
        this.applyCountsFromHtml(temp)

        // Defer button count refresh until variant updates finish, so spinner stays visible
        // this.refreshButtonCounts()

        // Update the grid wrapper content
        const newGridWrapper = temp.querySelector('[data-grid-wrapper]')
        const currentGridWrapper = this.wrapper
        if (newGridWrapper && currentGridWrapper) {
          currentGridWrapper.innerHTML = newGridWrapper.innerHTML
          // Re-setup handlers for new content
          this.setupProductCardHandlers()
          this.setupSortDropdown()
        }

        // Update the below-text content (outside the grid wrapper)
        const newBelowText = temp.querySelector('[data-below-text]')
        const currentBelowText = this.querySelector('[data-below-text]')
        if (currentBelowText && !newBelowText) {
          currentBelowText.remove()
        } else if (newBelowText) {
          if (currentBelowText) {
            currentBelowText.replaceWith(newBelowText.cloneNode(true))
          } else {
            const afterGrid = this.querySelector('[data-grid-wrapper]')
            if (afterGrid) afterGrid.insertAdjacentElement('afterend', newBelowText.cloneNode(true))
          }
        }
      }

      // Variant counts fetches - do these BEFORE clearing loading state
      try {
        const categoryParam = this.querySelector('.collection-header__filter[data-filter-group="category"]')?.dataset.paramName
        const modelParam = this.querySelector('.collection-header__filter[data-filter-group="model"]')?.dataset.paramName
        if (sectionKey && (categoryParam || modelParam)) {
          const current = new URL(url, window.location.origin)

          const buildVariant = (omitParam) => {
            const p = new URLSearchParams(current.search)
            if (!omitParam) return null
            let changed = false
            Array.from(p.keys()).forEach((key) => { if (key === omitParam) { p.delete(key); changed = true } })
            if (!changed) return null
            return this.buildCleanUrl(current.pathname, p)
          }

          const categoryVariantUrl = buildVariant(categoryParam)
          const modelVariantUrl = buildVariant(modelParam)

          const fetches = []
          if (modelVariantUrl) fetches.push(window.filterFetch?.fetchSections([sectionKey], modelVariantUrl))
          if (categoryVariantUrl) fetches.push(window.filterFetch?.fetchSections([sectionKey], categoryVariantUrl))

          if (fetches.length > 0) {
            const variantResults = await Promise.all(fetches)

            let idx = 0
            if (modelVariantUrl) {
              const html = variantResults[idx++]?.[sectionKey]
              if (html) {
                const tempM = document.createElement('div')
                tempM.innerHTML = html
                this.applyCountsFromHtml(tempM, new Set([modelParam]))
              }
            }
            if (categoryVariantUrl) {
              const html = variantResults[idx++]?.[sectionKey]
              if (html) {
                const tempC = document.createElement('div')
                tempC.innerHTML = html
                this.applyCountsFromHtml(tempC, new Set([categoryParam]))
              }
            }
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[CollectionFiltersGrid] counts refresh error', err)
      }

      // Now refresh button counts (after variants) so the number swaps when finalized
      this.refreshButtonCounts()

      // Update clear filter visibility after content swap
      this.updateClearFilterVisibility()

      // Wait a frame so DOM updates render before we exit busy state
      await new Promise((resolve) => requestAnimationFrame(() => resolve()))
      if (shouldScroll) {
        this.scrollToTop()
      }
      endLoading()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[CollectionFiltersGrid] fetchAndSwap error', e)
      if (shouldScroll) {
        this.scrollToTop()
      }
      endLoading()
    }
  }

  applyCountsFromHtml (rootEl, paramNamesToUpdate) {
    // Update dropdown option counts by reading from source dropdown lists
    const items = this.querySelectorAll('.collection-header__filters-item')
    items.forEach((item) => {
      const button = item.querySelector('.collection-header__filter')
      const paramName = button?.dataset.paramName
      const filterGroup = button?.dataset.filterGroup

      if (!paramName || (paramNamesToUpdate && !paramNamesToUpdate.has(paramName))) return

      const srcButton = rootEl.querySelector(`.collection-header__filters-item .collection-header__filter[data-param-name="${CSS.escape(paramName)}"]`)
      const srcItem = srcButton ? srcButton.closest('.collection-header__filters-item') : null
      if (!srcItem) return

      srcItem.querySelectorAll('.collection-header__filter-option-btn').forEach((srcBtn) => {
        const value = srcBtn.getAttribute('data-value')
        const countText = srcBtn.querySelector('.collection-header__filter-option-count-number')?.textContent || ''
        const dstBtn = item.querySelector(`.collection-header__filter-option-btn[data-value="${CSS.escape(value)}"]`)
        if (!dstBtn) return

        // Only skip updating the selected option on the main HTML update (no paramNamesToUpdate).
        // When paramNamesToUpdate is provided (variant counts), we DO update the selected option count.
        const isSelected = dstBtn.getAttribute('aria-pressed') === 'true'
        if (!paramNamesToUpdate && isSelected) return

        const dstCountEl = dstBtn.querySelector('.collection-header__filter-option-count-number')
        if (dstCountEl) dstCountEl.textContent = countText

        // Hide category filter options with 0 count
        if (filterGroup === 'category') {
          const dstLi = dstBtn.closest('.collection-header__filter-option')
          if (dstLi) {
            const countMatch = countText.match(/\((\d+)\)/)
            const count = countMatch ? parseInt(countMatch[1], 10) : 0
            dstLi.style.display = count === 0 ? 'none' : ''
          }
        }
      })
    })
  }

  shouldIncludeUniversalProducts (url) {
    const urlObj = new URL(url, window.location.origin)
    const params = new URLSearchParams(urlObj.search)

    // Categories that contain universal products (products without specific models)
    const universalCategories = ['apparel', 'security', 'storage-and-organisation']

    const modelParam = this.querySelector('.collection-header__filter[data-filter-group="model"]')?.dataset.paramName
    const categoryParam = this.querySelector('.collection-header__filter[data-filter-group="category"]')?.dataset.paramName

    if (!modelParam || !categoryParam) return { needed: false }

    const modelValue = params.get(modelParam)
    const categoryValue = params.get(categoryParam)

    // Only include universal products if both model+category are set and category contains universal products
    if (modelValue && categoryValue && universalCategories.includes(categoryValue)) {
      const universalParams = new URLSearchParams(urlObj.search)
      Array.from(universalParams.keys()).forEach((key) => {
        if (key === modelParam) universalParams.delete(key)
      })
      const universalUrl = this.buildCleanUrl(urlObj.pathname, universalParams)

      return { needed: true, universalUrl }
    }

    return { needed: false }
  }

  mergeGridResults (filteredHtml, universalHtml) {
    const filteredDoc = new DOMParser().parseFromString(filteredHtml, 'text/html')
    const universalDoc = new DOMParser().parseFromString(universalHtml, 'text/html')

    const filteredGrid = filteredDoc.querySelector('[data-grid]')
    const universalGrid = universalDoc.querySelector('[data-grid]')

    if (!filteredGrid || !universalGrid) return filteredHtml

    const universalProducts = Array.from(universalGrid.children)
    const filteredProductHandles = new Set()

    Array.from(filteredGrid.children).forEach(product => {
      const featuredHandle = product.getAttribute('data-featured-product')
      if (featuredHandle) {
        filteredProductHandles.add(featuredHandle)
        return
      }
      const link = product.querySelector('a[href*="/products/"]')
      if (link) {
        const handle = link.getAttribute('href').split('/products/')[1]?.split('?')[0]
        if (handle) filteredProductHandles.add(handle)
      }
    })

    universalProducts.forEach(product => {
      if (product.hasAttribute('data-featured-product')) return
      const link = product.querySelector('a[href*="/products/"]')
      if (link) {
        const handle = link.getAttribute('href').split('/products/')[1]?.split('?')[0]
        if (handle && !filteredProductHandles.has(handle)) {
          filteredGrid.appendChild(product.cloneNode(true))
        }
      }
    })

    const featuredCard = filteredGrid.querySelector('[data-featured-product]')
    if (featuredCard && featuredCard !== filteredGrid.firstElementChild) {
      filteredGrid.insertBefore(featuredCard, filteredGrid.firstElementChild)
    }

    return filteredDoc.documentElement.innerHTML
  }

  refreshButtonCounts () {
    // Update button counts to match the selected dropdown option counts
    this.querySelectorAll('.collection-header__filters-item').forEach((item) => {
      const button = item.querySelector('.collection-header__filter')
      const buttonCountEl = button?.querySelector('.collection-header__filter-count-number')
      if (!button || !buttonCountEl) return

      // Find the selected option in this filter group
      const selectedOption = item.querySelector('.collection-header__filter-option-btn[aria-pressed="true"]')
      if (!selectedOption) {
        // No selection, hide the button count
        buttonCountEl.classList.add('hidden')
        return
      }

      // Get the count from the selected dropdown option
      const optionCountEl = selectedOption.querySelector('.collection-header__filter-option-count-number')
      if (optionCountEl) {
        const optionCount = optionCountEl.textContent.trim()
        buttonCountEl.textContent = optionCount
        buttonCountEl.classList.remove('hidden')
        // Proactively hide spinner beside the count after update
        const spinner = button.querySelector('.collection-header__filter-count-spinner')
        if (spinner) spinner.classList.add('hidden')
      }
    })
  }

  getSelectionLabels () {
    const getLabel = (group) => {
      const item = this.querySelector(`.collection-header__filters-item .collection-header__filter[data-filter-group="${group}"]`)
      if (!item) return ''
      const active = item.parentElement?.querySelector('.collection-header__filter-option-btn[aria-pressed="true"] .collection-header__filter-option-label')
      return active?.textContent?.trim() || ''
    }
    const model = getLabel('model')
    const category = getLabel('category')
    return { model, category }
  }

  getSelectionSlugs () {
    const getSlug = (group) => {
      const item = this.querySelector(`.collection-header__filters-item .collection-header__filter[data-filter-group="${group}"]`)
      if (!item) return ''
      const active = item.parentElement?.querySelector('.collection-header__filter-option-btn[aria-pressed="true"]')
      return active?.dataset.slug || ''
    }
    return { model: getSlug('model'), category: getSlug('category') }
  }

  buildCanonicalUrl (currentUrl) {
    try {
      const url = new URL(currentUrl, window.location.origin)
      const params = new URLSearchParams(url.search)

      const modelParam = this.querySelector('.collection-header__filter[data-filter-group="model"]')?.dataset.paramName
      const categoryParam = this.querySelector('.collection-header__filter[data-filter-group="category"]')?.dataset.paramName

      const modelValue = modelParam ? params.get(modelParam) : null
      const categoryValue = categoryParam ? params.get(categoryParam) : null

      const hasModel = !!modelValue
      const hasCategory = !!categoryValue
      const hasBoth = hasModel && hasCategory

      const canonicalParams = new URLSearchParams()

      if (hasBoth) {
        if (modelParam && modelValue) {
          canonicalParams.set(modelParam, modelValue)
        }
      } else if (hasModel && modelParam && modelValue) {
        canonicalParams.set(modelParam, modelValue)
      } else if (hasCategory && categoryParam && categoryValue) {
        canonicalParams.set(categoryParam, categoryValue)
      }

      return this.buildCleanUrl(url.pathname, canonicalParams)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[CollectionFiltersGrid] buildCanonicalUrl error', e)
      return currentUrl
    }
  }

  updateSeoFromSelection (forceDefaults = false) {
    const { model, category } = this.getSelectionLabels()
    const { category: categorySlug } = this.getSelectionSlugs()
    const hasModel = !!model
    const hasCategory = !!category
    const h1El = this.querySelector('[data-dynamic-h1]')
    const titleModel = this.dataset.titleModelTemplate
    const titleCategory = this.dataset.titleCategoryTemplate
    const titleModelCategory = this.dataset.titleModelCategoryTemplate

    const h1Model = this.dataset.h1ModelTemplate
    const h1Category = this.dataset.h1CategoryTemplate
    const h1ModelCategory = this.dataset.h1ModelCategoryTemplate

    const fill = (tpl) => tpl?.replaceAll('{{ model }}', model).replaceAll('{{ category }}', category)
    const decodeAmp = (str) => str?.replaceAll('&amp;', '&') ?? ''
    const bespokeCategory = categorySlug && this.seoCategoryTitles?.[categorySlug]

    if (!forceDefaults && hasModel && hasCategory) {
      if (titleModelCategory) document.title = fill(titleModelCategory)
      this.setRobotsTag('noindex,follow')
      const canonicalUrl = this.buildCanonicalUrl(window.location.href)
      this.setCanonicalTag(canonicalUrl)
      if (h1El && h1ModelCategory) h1El.textContent = fill(h1ModelCategory)
      return
    }
    if (!forceDefaults && hasModel) {
      if (titleModel) document.title = fill(titleModel)
      this.setRobotsTag('index,follow')
      if (h1El && h1Model) h1El.textContent = fill(h1Model)
      return
    }
    if (!forceDefaults && hasCategory) {
      if (bespokeCategory?.title) document.title = decodeAmp(bespokeCategory.title)
      else if (titleCategory) document.title = fill(titleCategory)
      this.setRobotsTag('index,follow')
      if (h1El) {
        if (bespokeCategory?.h1) h1El.textContent = decodeAmp(bespokeCategory.h1)
        else if (h1Category) h1El.textContent = fill(h1Category)
      }
      return
    }
    this.setRobotsTag('index,follow')
    if (h1El && this.initialH1) h1El.textContent = this.initialH1
    if (this.defaultTitle) document.title = this.defaultTitle
  }

  setMetaTag (name, content) {
    if (!content) return
    let tag = document.head.querySelector(`meta[name="${name}"]`)
    if (!tag) {
      tag = document.createElement('meta')
      tag.setAttribute('name', name)
      document.head.appendChild(tag)
    }
    tag.setAttribute('content', content)
  }

  setRobotsTag (content) {
    let tag = document.head.querySelector('meta[name="robots"]')
    if (!tag) {
      tag = document.createElement('meta')
      tag.setAttribute('name', 'robots')
      document.head.appendChild(tag)
    }
    tag.setAttribute('content', content)
  }

  getMetaTag (name) {
    const tag = document.head.querySelector(`meta[name="${name}"]`)
    return tag?.getAttribute('content') || ''
  }

  setupProductCardHandlers () {
    // Add click handlers to product cards to store filter state
    const productCards = this.querySelectorAll('.product-card')
    productCards.forEach(card => {
      card.addEventListener('click', () => {
        this.storeFilterState()
      })
    })
  }

  handleStickyBehavior () {
    this.stickyFilters = this.querySelector('.collection-header__filters-sticky')
    if (!this.stickyFilters) return

    if (this.stickyBehavior === 'static') {
      this.stickyFilters.style.position = 'relative'
      this.stickyFilters.style.top = 'auto'
      return
    }

    this.setupStickyPositioning()

    if (this.stickyBehavior === 'sticky_hide_on_scroll') {
      this.setupStickyHideOnScroll()
    }
  }

  setupStickyPositioning () {
    setTimeout(() => {
      this.updateStickyPosition()
    }, 100)

    document.addEventListener('promobar:visibility', () => {
      setTimeout(() => {
        this.updateStickyPosition()
      }, 50)
    })

    const observer = new MutationObserver(() => {
      this.updateStickyPosition()
    })

    const promoBarContainers = document.querySelectorAll('.promo-bar-container')
    promoBarContainers.forEach(container => {
      observer.observe(container, {
        childList: true,
        attributes: true,
        subtree: true,
        attributeFilter: ['class', 'style'],
      })
    })

    window.addEventListener('resize', () => {
      this.updateStickyPosition()
    })

    this.stickyPositionObserver = observer
  }

  updateStickyPosition () {
    if (!this.stickyFilters) return

    const promoBar = document.querySelector('promo-bar:not(.hidden)')
    const isPromoBarVisible = promoBar &&
      !promoBar.classList.contains('hidden') &&
      promoBar.offsetHeight > 0 &&
      getComputedStyle(promoBar).display !== 'none'

    const headerHeight = 60

    if (isPromoBarVisible) {
      const promoBarHeight = Math.ceil(promoBar.getBoundingClientRect().height)
      this.stickyTopPosition = headerHeight + promoBarHeight
    } else {
      this.stickyTopPosition = headerHeight
    }

    this.stickyFilters.style.top = `${this.stickyTopPosition}px`
  }

  setupStickyHideOnScroll () {
    if (!this.stickyFilters) return

    this.lastScrollY = window.scrollY
    this.filtersHidden = false
    this.scrollThreshold = 10
    this.stickyStartScrollY = null

    this.stickyFilters.style.transition = 'transform 0.3s ease-out'

    const sentinel = document.createElement('div')
    sentinel.className = 'collection-header__sticky-sentinel'
    sentinel.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 1px; pointer-events: none;'
    this.stickyFilters.parentElement.insertBefore(sentinel, this.stickyFilters)
    this.stickySentinel = sentinel

    this.stickyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const wasSticky = this.isSticky
          this.isSticky = !entry.isIntersecting

          if (this.isSticky && !wasSticky) {
            this.stickyStartScrollY = window.scrollY
          } else if (!this.isSticky) {
            this.stickyStartScrollY = null
            this.showFilters()
          }
        })
      },
      {
        threshold: 0,
        rootMargin: `-${this.stickyTopPosition || 60}px 0px 0px 0px`,
      },
    )

    this.stickyObserver.observe(sentinel)

    this.handleScroll = () => {
      if (!this.isSticky) return

      const currentScrollY = window.scrollY
      const scrollDelta = currentScrollY - this.lastScrollY
      const filterHeight = this.stickyFilters.offsetHeight

      if (Math.abs(scrollDelta) < this.scrollThreshold) return

      const scrolledPastFilters = this.stickyStartScrollY !== null &&
        (currentScrollY - this.stickyStartScrollY) > filterHeight

      if (scrollDelta > 0 && !this.filtersHidden && scrolledPastFilters) {
        this.hideFilters()
      } else if (scrollDelta < 0 && this.filtersHidden) {
        this.showFilters()
      }

      this.lastScrollY = currentScrollY
    }

    window.addEventListener('scroll', this.handleScroll, { passive: true })
  }

  hideFilters () {
    if (!this.stickyFilters || this.filtersHidden) return
    const filterHeight = this.stickyFilters.offsetHeight
    this.stickyFilters.style.transform = `translateY(-${filterHeight + (this.stickyTopPosition || 60)}px)`
    this.filtersHidden = true
  }

  showFilters () {
    if (!this.stickyFilters || !this.filtersHidden) return
    this.stickyFilters.style.transform = 'translateY(0)'
    this.filtersHidden = false
  }

  async fetchPriceBracketCounts () {
    if (!this.priceFilterEnabled || this.priceBracketCountsFetching) return

    const priceFilterItem = this.querySelector('.collection-header__filters-item--price')
    if (!priceFilterItem) return

    const bracketButtons = priceFilterItem.querySelectorAll('.collection-header__filter-option-btn[data-gte]')
    if (bracketButtons.length === 0) return

    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)
    params.delete('filter.v.price.gte')
    params.delete('filter.v.price.lte')
    params.delete('page')
    const baseUrl = this.buildCleanUrl(url.pathname, params)
    const cacheKey = baseUrl

    if (this.priceBracketCountsCache.has(cacheKey)) {
      const cachedCounts = this.priceBracketCountsCache.get(cacheKey)
      this.applyPriceBracketCounts(bracketButtons, cachedCounts)
      return
    }

    this.priceBracketCountsFetching = true

    bracketButtons.forEach(btn => {
      const countNum = btn.querySelector('.collection-header__filter-option-count-number')
      const countSpinner = btn.querySelector('.collection-header__filter-option-count-spinner')
      if (countNum) countNum.classList.add('hidden')
      if (countSpinner) countSpinner.classList.remove('hidden')
    })

    const sectionDomId = this.closest('[id^="shopify-section-"]')?.id
    const sectionKey = sectionDomId ? sectionDomId.replace(/^shopify-section-/, '') : null

    if (!sectionKey || !window.filterFetch?.fetchSections) {
      this.priceBracketCountsFetching = false
      return
    }

    try {
      const fetchPromises = Array.from(bracketButtons).map(async (btn) => {
        const gte = btn.dataset.gte || ''
        const lte = btn.dataset.lte || ''

        const bracketParams = new URLSearchParams(params)
        if (gte) bracketParams.set('filter.v.price.gte', gte)
        if (lte) bracketParams.set('filter.v.price.lte', lte)

        const bracketUrl = this.buildCleanUrl(url.pathname, bracketParams)

        const data = await window.filterFetch.fetchSections([sectionKey], bracketUrl)
        if (!data || !data[sectionKey]) return { gte, lte, count: 0 }

        const temp = document.createElement('div')
        temp.innerHTML = data[sectionKey]

        const countEl = temp.querySelector('[data-product-count]')
        let count = 0
        if (countEl) {
          const match = countEl.textContent.match(/(\d+)/)
          if (match) count = parseInt(match[1], 10)
        }

        return { gte, lte, count }
      })

      const results = await Promise.all(fetchPromises)

      const countsMap = new Map()
      results.forEach(result => {
        if (result) {
          countsMap.set(`${result.gte}:${result.lte}`, result.count)
        }
      })

      this.priceBracketCountsCache.set(cacheKey, countsMap)
      this.applyPriceBracketCounts(bracketButtons, countsMap)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[CollectionFiltersGrid] fetchPriceBracketCounts error', e)
    } finally {
      this.priceBracketCountsFetching = false
    }
  }

  applyPriceBracketCounts (bracketButtons, countsMap) {
    bracketButtons.forEach(btn => {
      const gte = btn.dataset.gte || ''
      const lte = btn.dataset.lte || ''
      const key = `${gte}:${lte}`
      const count = countsMap.get(key) ?? 0

      const countNum = btn.querySelector('.collection-header__filter-option-count-number')
      const countSpinner = btn.querySelector('.collection-header__filter-option-count-spinner')

      if (countNum) {
        countNum.textContent = `(${count})`
        countNum.classList.remove('hidden')
      }
      if (countSpinner) {
        countSpinner.classList.add('hidden')
      }
    })
  }

  invalidatePriceBracketCountsCache () {
    this.priceBracketCountsCache.clear()
  }

  disconnectedCallback () {
    this.unbindDocumentOutsideClick()

    if (this.stickyPositionObserver) {
      this.stickyPositionObserver.disconnect()
    }

    if (this.stickyObserver) {
      this.stickyObserver.disconnect()
    }

    if (this.handleScroll) {
      window.removeEventListener('scroll', this.handleScroll)
    }

    if (this.stickySentinel) {
      this.stickySentinel.remove()
    }
  }

  storeFilterState () {
    // Get current filter state from URL
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)
    const filterState = {}

    // Store all filter parameters
    Array.from(params.keys()).forEach(key => {
      if (key.startsWith('filter.')) {
        filterState[key] = params.get(key)
      }
    })

    // Only store if there are active filters
    if (Object.keys(filterState).length > 0) {
      try {
        localStorage.setItem('tentbox_collection_filters', JSON.stringify(filterState))
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[CollectionFiltersGrid] Failed to store filter state:', e)
      }
    }
  }

  restoreFilterState () {
    try {
      const storedState = localStorage.getItem('tentbox_collection_filters')
      if (!storedState) return false

      const filterState = JSON.parse(storedState)
      const url = new URL(window.location.href)
      const params = new URLSearchParams(url.search)

      // Remove load_previous param
      params.delete('load_previous')

      // Apply stored filter parameters
      Object.keys(filterState).forEach(key => {
        params.set(key, filterState[key])
      })

      // Update URL and trigger filter change
      const nextUrl = this.buildCleanUrl(url.pathname, params)
      window.history.replaceState({}, '', nextUrl)

      // Clear the stored state after using it
      localStorage.removeItem('tentbox_collection_filters')

      return true
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[CollectionFiltersGrid] Failed to restore filter state:', e)
      return false
    }
  }

  checkForLoadPrevious () {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)

    if (params.has('load_previous')) {
      const restored = this.restoreFilterState()
      if (restored) {
        // Sync UI with restored filters and fetch new content
        this.syncFromUrl()
        const cleanUrl = this.buildCleanUrl(url.pathname, new URLSearchParams(window.location.search))
        this.fetchAndSwap(cleanUrl, false) // Don't scroll on restore
        return true
      } else {
        // If no stored state, just remove the load_previous param
        params.delete('load_previous')
        const cleanUrl = this.buildCleanUrl(url.pathname, params)
        window.history.replaceState({}, '', cleanUrl)
      }
    }
    return false
  }
}

if (!customElements.get('collection-filters-grid')) {
  customElements.define('collection-filters-grid', CollectionFiltersGrid)
}
